import { type ElementType } from 'react';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import { useGetOrders } from '@hungryroot/rest-hooks';
import {
  SUPPORT_ARTICLE_QUICK_TOUR_ROUTE,
  SUPPORT_ARTICLE_SHOP_WITH_CREDITS_ROUTE,
  SUPPORT_ARTICLE_FIRST_DELIVERY_ROUTE,
  SUPPORT_ARTICLE_FOOD_PROFILE_ROUTE,
  SUPPORT_ARTICLE_SCHEDULE_MANAGEMENT_ROUTE,
  SUPPORT_ARTICLE_DELIVERY_ISSUES_ROUTE,
  SUPPORT_ARTICLE_EDIT_ORDER_ROUTE,
  SUPPORT_ARTICLE_EDIT_PLAN_ROUTE,
} from '@hungryroot/routes';
import {
  CaretRight,
  CaretLeft,
  Map as MapIcon,
  Credit,
  BookWithApple,
  Calendar3,
  TruckWithExclamation,
  PencilAndPaper,
  Box3,
  BoxWithPencil,
  Heading,
  Paragraph,
  IconButton,
  TransparentButton,
} from '@hungryroot/ui-kit';
import { Carousel, CarouselTrack, CarouselSlide } from '../Carousel/Carousel';
import { useCarouselControls } from '../Carousel/useCarouselControls';

import styles from './GettingStartHelpCenterSection.module.css';

const SLIDES_TO_SHOW = 4;
const SLIDE_GAP = 16;

type GettingStartHelpCenterSectionProps = {
  title: string;
  className?: string;
};

export function GettingStartHelpCenterSection(
  props: GettingStartHelpCenterSectionProps
) {
  const { title, className } = props;

  const carousel = useCarouselControls();
  const cards = useCards();

  if (cards.length === 0) {
    return null;
  }

  return (
    <div className={clsx(styles['container'], className)}>
      <div className={styles['section-header']}>
        <div>
          <Heading variant="H2" className={styles['section-title']}>
            {title}
          </Heading>
          <Paragraph variant="body">
            Tips to get the most out of your Hungryroot
          </Paragraph>
        </div>
        {cards.length > SLIDES_TO_SHOW ? (
          <div className={styles['section-header-arrow-container']}>
            <IconButton
              style={carousel.getIsBackDisabled() ? 'disabled' : 'muted'}
              icon={<CaretLeft />}
              prominence="secondary"
              size="small"
              ariaLabel="Previous slide"
              onClick={carousel.back}
            />
            <IconButton
              style={carousel.getIsNextDisabled() ? 'disabled' : 'muted'}
              icon={<CaretRight />}
              prominence="secondary"
              size="small"
              ariaLabel="Next slide"
              onClick={carousel.next}
            />
          </div>
        ) : null}
      </div>

      <Carousel
        totalSlides={cards.length}
        slidesToShow={SLIDES_TO_SHOW}
        slidesToScroll={SLIDES_TO_SHOW}
        carouselRef={carousel.ref}
        onSlideChange={carousel.onSlideChange}
        ariaLabel="Getting started help center"
        className={styles['desktop-carousel']}
      >
        {(carouselState) => (
          <CarouselTrack
            carousel={carouselState}
            gap={SLIDE_GAP}
            className={styles['carousel-track']}
          >
            {cards.map((card, index) => {
              return (
                <CarouselSlide
                  key={card.href}
                  carousel={carouselState}
                  index={index}
                  gap={SLIDE_GAP}
                >
                  <Slide
                    isActive={carouselState.activeIndexes.includes(index)}
                    isDragging={carouselState.isDragging}
                    {...card}
                  />
                </CarouselSlide>
              );
            })}
          </CarouselTrack>
        )}
      </Carousel>

      <div className={styles['mobile-slider-container']}>
        {cards.map((card) => (
          <Slide key={card.href} isDragging={false} {...card} />
        ))}
      </div>
    </div>
  );
}

type SlideProps = {
  icon: ElementType;
  title: string;
  subtitle: string;
  href: string;
  isDragging: boolean;
  isActive?: boolean;
};

function Slide(props: SlideProps) {
  const {
    icon: Icon,
    title,
    subtitle,
    href,
    isDragging,
    isActive = true,
  } = props;

  const history = useHistory();

  return (
    <TransparentButton
      className={clsx(styles.slide, { [styles.hidden]: !isActive })}
      onClick={() => {
        if (!isDragging) {
          history.push(href);
        }
      }}
    >
      <Icon className={styles['slide-icon']} />
      <div>
        <Heading variant="H6" className={styles['slide-title']}>
          {title}
        </Heading>
        <Paragraph variant="caption" className={styles['slide-subtitle']}>
          {subtitle}
        </Paragraph>
      </div>
    </TransparentButton>
  );
}

type Card = {
  icon: ElementType;
  title: string;
  subtitle: string;
  href: string;
};

const CardToPageMap = new Map<string, Card>([
  [
    SUPPORT_ARTICLE_QUICK_TOUR_ROUTE,
    {
      icon: MapIcon,
      title: 'A quick tour',
      subtitle: 'How to order and where to find things.',
      href: SUPPORT_ARTICLE_QUICK_TOUR_ROUTE,
    },
  ],
  [
    SUPPORT_ARTICLE_SHOP_WITH_CREDITS_ROUTE,
    {
      icon: Credit,
      title: 'Shop with credits',
      subtitle: 'How credits work at Hungryroot.',
      href: SUPPORT_ARTICLE_SHOP_WITH_CREDITS_ROUTE,
    },
  ],
  [
    SUPPORT_ARTICLE_FIRST_DELIVERY_ROUTE,
    {
      icon: Box3,
      title: 'Your first delivery',
      subtitle: 'How and when your delivery will arrive.',
      href: SUPPORT_ARTICLE_FIRST_DELIVERY_ROUTE,
    },
  ],
  [
    SUPPORT_ARTICLE_FOOD_PROFILE_ROUTE,
    {
      icon: BookWithApple,
      title: 'Your food profile',
      subtitle: 'How to help us build you a better box each week.',
      href: SUPPORT_ARTICLE_FOOD_PROFILE_ROUTE,
    },
  ],
  [
    SUPPORT_ARTICLE_SCHEDULE_MANAGEMENT_ROUTE,
    {
      icon: Calendar3,
      title: 'Manage your schedule',
      subtitle: 'Make your delivery schedule work for you.',
      href: SUPPORT_ARTICLE_SCHEDULE_MANAGEMENT_ROUTE,
    },
  ],
  [
    SUPPORT_ARTICLE_DELIVERY_ISSUES_ROUTE,
    {
      icon: TruckWithExclamation,
      title: 'Delivery issues',
      subtitle: "What to do if something's not right.",
      href: SUPPORT_ARTICLE_DELIVERY_ISSUES_ROUTE,
    },
  ],
  [
    SUPPORT_ARTICLE_EDIT_ORDER_ROUTE,
    {
      icon: BoxWithPencil,
      title: 'Edit your order',
      subtitle: 'Add, remove, or swap items in your order.',
      href: SUPPORT_ARTICLE_EDIT_ORDER_ROUTE,
    },
  ],
  [
    SUPPORT_ARTICLE_EDIT_PLAN_ROUTE,
    {
      icon: PencilAndPaper,
      title: 'Edit your plan',
      subtitle: 'Adjust how much food you get each week.',
      href: SUPPORT_ARTICLE_EDIT_PLAN_ROUTE,
    },
  ],
]);

const firstOrderPendingCards = [
  SUPPORT_ARTICLE_QUICK_TOUR_ROUTE,
  SUPPORT_ARTICLE_SHOP_WITH_CREDITS_ROUTE,
  SUPPORT_ARTICLE_FIRST_DELIVERY_ROUTE,
  SUPPORT_ARTICLE_FOOD_PROFILE_ROUTE,
];

const firstOrderSubmittedCards = [
  SUPPORT_ARTICLE_QUICK_TOUR_ROUTE,
  SUPPORT_ARTICLE_SHOP_WITH_CREDITS_ROUTE,
  SUPPORT_ARTICLE_FIRST_DELIVERY_ROUTE,
  SUPPORT_ARTICLE_SCHEDULE_MANAGEMENT_ROUTE,
  SUPPORT_ARTICLE_FOOD_PROFILE_ROUTE,
];

const firstOrderDeliveredCards = [
  SUPPORT_ARTICLE_DELIVERY_ISSUES_ROUTE,
  SUPPORT_ARTICLE_SHOP_WITH_CREDITS_ROUTE,
  SUPPORT_ARTICLE_EDIT_ORDER_ROUTE,
  SUPPORT_ARTICLE_SCHEDULE_MANAGEMENT_ROUTE,
  SUPPORT_ARTICLE_EDIT_PLAN_ROUTE,
  SUPPORT_ARTICLE_FOOD_PROFILE_ROUTE,
];

function useCards() {
  const { isLoading: isCurrentOrderLoading, data: currentOrderList } =
    useGetOrders({
      time_bucket: 'current',
      limit: 1,
    });

  const { isLoading: isPastOrderLoading, data: pastOrderList } = useGetOrders({
    time_bucket: 'past',
    limit: 1,
  });

  if (isCurrentOrderLoading || isPastOrderLoading) {
    return [];
  }

  const currentOrder = currentOrderList?.results[0];
  const pastOrder = pastOrderList?.results[0];

  let cards;

  const isCurrentOrderTheFirstOrder = currentOrder?.sequence === 1;

  if (pastOrder) {
    cards = firstOrderDeliveredCards;
  } else if (isCurrentOrderTheFirstOrder) {
    cards = firstOrderSubmittedCards;
  } else {
    cards = firstOrderPendingCards;
  }

  return cards.map((card) => CardToPageMap.get(card)).filter(Boolean) as Card[];
}
